import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export type AccountType = 'parent' | 'organisation' | 'other';

interface UserProfile {
  id: string;
  nom: string | null;
  prenom: string | null;
  adresse: string | null;
  cpostal: string | null;
  localite: string | null;
  telephone: string | null;
  telephone2: string | null;
  nnational: string | null;
  newsletter: boolean;
  /* Date de la derniere invitation refusee, pour ne pas la reposer chaque semaine. */
  newsletter_proposee_le?: string | null;
  avatar_url: string | null;
  role: string;
  account_type: AccountType;
  organisation_name: string | null;
  company_number: string | null;
  is_legal_guardian: boolean;
  has_new_registration_notification: boolean;
}

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  /**
   * Passe a true des qu'une tentative de chargement du profil a abouti ou
   * echoue. Les gardes de route s'en servent pour attendre au lieu de rejeter
   * un utilisateur dont le role n'est pas encore connu. `isLoading` ne convient
   * pas : il vaut false a l'initialisation et n'est leve que par les actions.
   */
  profileLoaded: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ user: User | null, error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, options?: { redirectTo?: string }) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  isAdmin: () => boolean;
  clearRegistrationNotification: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  isLoading: false,
  profileLoaded: false,
  error: null,

  // Une deconnexion invalide le profil charge : on repasse profileLoaded a
  // false pour que le prochain utilisateur ne herite pas de cet etat.
  setUser: (user) => set(user ? { user } : { user: null, profile: null, profileLoaded: false }),
  setProfile: (profile) => set({ profile, profileLoaded: true }),
  
  signIn: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user }, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (error) throw error;
      
      // Get user profile and role
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        set({ error: profileError.message });
        return { error: new Error(profileError.message) };
      }

      // Clean nnational field from sentinel values
      if (profileData && profileData.nnational) {
        if (['EMPTY', '000', 'NULL'].includes(profileData.nnational)) {
          profileData.nnational = null;
        }
      }

      set({ user, profile: profileData, profileLoaded: true });
      return { error: null };
    } catch (error: any) {
      set({ error: error.message });
      return { error };
    } finally {
      set({ isLoading: false });
    }
  },
  
  signUp: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });
      
      if (error) throw error;
      
      // Return the user object so the component can check email confirmation status
      return { user: data.user, error: null };
    } catch (error: any) {
      set({ error: error.message });
      return { user: null, error };
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Redirige vers Google ; au retour, `/auth/callback` attend la session
  // (detectee dans l'URL par le client) puis le profil avant d'orienter selon
  // le role. Un compte existant avec la meme adresse est relie par Supabase
  // (liaison automatique), pas duplique.
  signInWithGoogle: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        // Laisse choisir le compte : une famille partage souvent un navigateur.
        queryParams: { prompt: 'select_account' },
      },
    });
    return { error: error ?? null };
  },

  signOut: async () => {
    set({ isLoading: true, error: null });
    try {
      // First check if we have a session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // If no session, just clear local state
        set({ user: null, profile: null, profileLoaded: false });
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null, profile: null, profileLoaded: false });
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  resetPassword: async (email, options = {}) => {
    set({ isLoading: true, error: null });
    try {
      const redirectTo = options.redirectTo || `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  updatePassword: async (password) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchProfile: async () => {
    const { user } = get();
    if (!user) {
      set({ profile: null, profileLoaded: true });
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) throw error;

      // Clean nnational field from sentinel values
      if (data && data.nnational) {
        if (['EMPTY', '000', 'NULL'].includes(data.nnational)) {
          data.nnational = null;
        }
      }

      set({ profile: data });
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      set({ error: error.message });
    } finally {
      // profileLoaded passe a true meme en cas d'echec : une garde qui attend
      // doit finir par trancher, sinon l'interface reste bloquee sur l'ecran
      // de chargement.
      set({ isLoading: false, profileLoaded: true });
    }
  },
  
  updateProfile: async (updates) => {
    const { user } = get();
    if (!user) return;
    
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Refresh profile data
      await get().fetchProfile();
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  isAdmin: () => {
    const { profile } = get();
    return profile?.role === 'admin';
  },

  clearRegistrationNotification: async () => {
    const { user, profile } = get();
    if (!user || !profile) return;
    
    try {
      // Update the database
      const { error } = await supabase
        .from('users')
        .update({ has_new_registration_notification: false })
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Update the local state
      set({ 
        profile: { 
          ...profile, 
          has_new_registration_notification: false 
        } 
      });
    } catch (error: any) {
      console.error('Error clearing registration notification:', error);
    }
  }
}));